import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import TimelineEntryShell from './TimelineEntryShell';
import TimelineEntryAttribution from './TimelineEntryAttribution';
import TimelineDisclosureToggle from './TimelineDisclosureToggle';
import TimelineMultipleMomentsIndicator from './TimelineMultipleMomentsIndicator';
import SourceStoryIcon from './SourceStoryIcon';
import StoryMedicationReviewScene from './StoryMedicationReviewScene';
import StoryMedicationReviewBadge from './StoryMedicationReviewBadge';

const STORY_TEXT_PRIMARY = '#1F2937';
const STORY_TEXT_SECONDARY = '#4B5563';

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const parseDateKey = (value = '') => {
  const dateKey = normalizeText(value);
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatMomentDateLabel = (value = '') => {
  const date = parseDateKey(value);
  return date
    ? date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '';
};

const getSceneTitle = (scene = {}, presentation = {}) => normalizeText(
  scene.title
  || scene.titlePrefix
  || presentation.categoryLabel
  || scene.categoryLabel
  || scene.category
  || 'Moment'
);

const getSceneBody = (scene = {}) => normalizeText(
  scene.body
  || scene.content
  || scene.text
  || scene.note
  || scene.description
  || scene.summary
  || scene.sourceText
);

const StoryScene = ({ scene, presentation = {}, compact = false, isLast = false, authoredSectionText = '', showCategoryLabel = true, showBody = true }) => {
  const title = getSceneTitle(scene, presentation);
  const body = getSceneBody(scene);
  const categoryLabel = normalizeText(presentation.categoryLabel || scene.categoryLabel || scene.category);
  const occurredOnDateLabel = formatMomentDateLabel(scene.occurredOnDate || scene.metadata?.timelinePlanner?.occurredOnDate || '');
  const normalizedSectionText = normalizeText(authoredSectionText).toLowerCase();
  const shouldShowBody = body
    && body.toLowerCase() !== title.toLowerCase()
    && (!normalizedSectionText || !normalizedSectionText.includes(body.toLowerCase()));

  return (
    <Box
      data-testid="story-scene"
      data-scene-id={scene.id || ''}
      sx={{
        py: compact ? 0.55 : 0.75,
        borderBottom: isLast ? 'none' : `1px solid ${alpha(colors.semantic.line, 0.5)}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {showCategoryLabel && categoryLabel && categoryLabel.toLowerCase() !== title.toLowerCase() ? (
          <Typography sx={{ mb: 0.12, fontSize: '0.64rem', fontWeight: 750, color: STORY_TEXT_SECONDARY }}>
            {categoryLabel}
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: compact ? '0.84rem' : '0.9rem', fontWeight: 800, color: STORY_TEXT_PRIMARY }}>
          {title}
        </Typography>
        {occurredOnDateLabel ? (
          <Typography sx={{ mt: 0.08, fontSize: '0.68rem', fontWeight: 700, color: STORY_TEXT_SECONDARY }}>
            {occurredOnDateLabel}
          </Typography>
        ) : null}
        {showBody && shouldShowBody ? (
          <Typography sx={{ mt: 0.2, fontSize: compact ? '0.8rem' : '0.84rem', lineHeight: 1.45, color: STORY_TEXT_SECONDARY }}>
            {body}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

const StoryCard = ({
  storyItem = {},
  storySections = [],
  storySummary = null,
  getEntryPresentation = () => ({}),
  expanded = true,
  onToggleExpanded = () => {},
  previewText = '',
  receiptSummary = '',
  titlesOnly = false,
  originalSourceNode = null,
  menuActions = [],
  showActions = false,
  actionMenuDisabled = false,
  childName = '',
}) => {
  const scenes = Array.isArray(storyItem.sourceStoryScenes) ? storyItem.sourceStoryScenes : [];
  const [chapterTextExpanded, setChapterTextExpanded] = React.useState(false);
  const sourceStoryId = storyItem.sourceNoteId || storyItem.id || '';
  const storySummaryText = normalizeText(storySummary?.text || '');
  const receiptText = normalizeText(receiptSummary);
  const preview = normalizeText(receiptText || previewText || storySummaryText || scenes[0]?.body || scenes[0]?.title || '');
  const storyTitle = storyItem.storyDisplayLabel || storyItem.sourceStoryLabel || 'Source story';
  const storySourceLabel = normalizeText(storyItem.sourceFileName || '');
  const storyPresentation = getEntryPresentation(scenes[0] || storyItem || {});
  const storyMomentCount = Number.isFinite(Number(storyItem.sourceStorySceneCount)) && Number(storyItem.sourceStorySceneCount) > 0
    ? Number(storyItem.sourceStorySceneCount)
    : scenes.length;
  const hasChapterText = Boolean(storySummaryText) || storySections.some((section) => (
    normalizeText(section.bodyText || '')
    && !/\bmedications?\b/i.test(section.label || '')
  ));
  const chapterTextId = React.useId();
  const medicationReviewListRef = React.useRef(null);
  const [medicationReviewStatuses, setMedicationReviewStatuses] = React.useState({});
  const pendingMedicationReviewCount = scenes.filter((scene) => {
    const persistedStatus = scene.metadata?.medicationAdministrationReview?.status || '';
    const currentStatus = medicationReviewStatuses[scene.id] || persistedStatus;
    return currentStatus === 'pending';
  }).length;

  const handleMedicationReviewStatusChange = React.useCallback((sceneId, status) => {
    if (!sceneId || !status) return;
    setMedicationReviewStatuses((current) => (
      current[sceneId] === status ? current : { ...current, [sceneId]: status }
    ));
  }, []);

  const handleOpenMedicationReviews = React.useCallback((event) => {
    event.stopPropagation();
    if (!expanded) onToggleExpanded();
    window.setTimeout(() => {
      medicationReviewListRef.current
        ?.querySelector('[data-medication-review-status="pending"]')
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, [expanded, onToggleExpanded]);

  const titleAccessoryNode = (
    <Stack direction="row" spacing={0.55} alignItems="center" sx={{ flexShrink: 0 }}>
      {storyMomentCount > 1 ? (
        <TimelineMultipleMomentsIndicator momentCount={storyMomentCount} compact />
      ) : null}
      <StoryMedicationReviewBadge
        count={pendingMedicationReviewCount}
        onClick={handleOpenMedicationReviews}
      />
      {expanded ? (
        <ExpandLessIcon sx={{ fontSize: 18, color: STORY_TEXT_SECONDARY }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 18, color: STORY_TEXT_SECONDARY }} />
      )}
    </Stack>
  );

  React.useEffect(() => {
    setChapterTextExpanded(false);
  }, [sourceStoryId]);

  const storyBodyNode = expanded ? (
    <Stack spacing={1.05} sx={{ minWidth: 0 }}>
      {preview ? (
        <Typography sx={{ fontSize: '0.8rem', color: STORY_TEXT_SECONDARY, fontWeight: 600, lineHeight: 1.45 }}>
          {receiptText || storySummaryText || preview}
        </Typography>
      ) : null}

      {originalSourceNode ? (
        <Box sx={{ minWidth: 0 }}>
          {originalSourceNode}
        </Box>
      ) : null}

      {hasChapterText && !titlesOnly ? (
        <TimelineDisclosureToggle
          expanded={chapterTextExpanded}
          onClick={() => setChapterTextExpanded((current) => !current)}
          ariaLabel={chapterTextExpanded ? 'Hide note text' : 'Show note text'}
          label={chapterTextExpanded ? 'Hide note text' : 'Show note text'}
          compact
          sx={{ alignSelf: 'flex-start', mt: 0.15 }}
        />
      ) : null}

      <Stack id={chapterTextId} ref={medicationReviewListRef} spacing={1.3}>
        {storySections.map((section) => {
          const isChapter = section.kind === 'chapter';
          const authoredSectionText = normalizeText(section.bodyText || '');
          const isMedicationChapter = /\bmedications?\b/i.test(section.label || '');
          const visibleAuthoredSectionText = chapterTextExpanded && !isMedicationChapter && !titlesOnly
            ? authoredSectionText
            : '';

          return (
            <Box
              key={section.key}
              data-testid={isChapter ? 'story-chapter' : 'story-compact-moment'}
              data-time-bucket={section.bucket}
            >
              <Typography sx={{
                mb: visibleAuthoredSectionText ? 0.3 : (isChapter ? 0.45 : 0.15),
                fontSize: isChapter ? '0.95rem' : '0.68rem',
                fontWeight: isChapter ? 850 : 750,
                letterSpacing: isChapter ? 0 : '0.055em',
                textTransform: isChapter ? 'none' : 'uppercase',
                color: isChapter ? STORY_TEXT_PRIMARY : STORY_TEXT_SECONDARY,
              }}>
                {section.label}
              </Typography>
              {visibleAuthoredSectionText ? (
                <Typography sx={{ mb: section.scenes.length ? 0.45 : 0, fontSize: '0.88rem', lineHeight: 1.58, color: STORY_TEXT_SECONDARY }}>
                  {visibleAuthoredSectionText}
                </Typography>
              ) : null}
              {section.scenes.length ? (
                <Box sx={{
                  pl: visibleAuthoredSectionText ? 0.35 : 0,
                  borderLeft: visibleAuthoredSectionText ? `2px solid ${alpha(colors.semantic.line, 0.6)}` : 'none',
                }}>
                  {section.scenes.map((scene, index) => (
                    <StoryMedicationReviewScene
                      key={scene.id || `${section.key}-${index}`}
                      scene={scene}
                      childName={childName}
                      onStatusChange={handleMedicationReviewStatusChange}
                    >
                      <StoryScene
                        scene={scene}
                        presentation={getEntryPresentation(scene)}
                        compact
                        authoredSectionText={visibleAuthoredSectionText}
                        showCategoryLabel={isMedicationChapter}
                        showBody={!titlesOnly}
                        isLast={index === section.scenes.length - 1}
                      />
                    </StoryMedicationReviewScene>
                  ))}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  ) : (
    <Typography sx={{ fontSize: '0.8rem', color: STORY_TEXT_SECONDARY, fontWeight: 600, lineHeight: 1.45 }}>
      {preview}
    </Typography>
  );

  return (
    <TimelineEntryShell
      presentation={storyPresentation}
      variant="story"
      title={storyTitle}
      titleAccessoryNode={titleAccessoryNode}
      metaNode={(
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          {storySourceLabel ? (
            <Typography sx={{ fontSize: '0.75rem', color: STORY_TEXT_SECONDARY, fontWeight: 600, lineHeight: 1.35 }}>
              {storySourceLabel}
            </Typography>
          ) : null}
          <TimelineEntryAttribution entry={storyItem} />
        </Stack>
      )}
      bodyNode={storyBodyNode}
      leadingNode={<SourceStoryIcon momentCount={storyMomentCount} />}
      menuActions={menuActions}
      showActions={showActions}
      actionMenuDisabled={actionMenuDisabled}
      onHeaderClick={onToggleExpanded}
      headerExpanded={expanded}
    />
  );
};

export default StoryCard;
